import { isTopic } from '@tunein/web-common';
import { wait } from '@tunein/web-utils';
import { forEach } from 'es-toolkit/compat';
import { v4 as uuidv4 } from 'uuid';
import { AD_BREAK_TITLE, BOOST_ENABLED, BOOST_INTRO_TITLE, BOOST_OUTRO_TITLE, MIDROLL_SUBTITLE, PLAYER_SUBSCRIPTION_QR_CODE_ENABLED, PLAYER_SUBSCRIPTION_QR_CODE_IMAGE_URL, PLAYER_SUBSCRIPTION_QR_CODE_LABEL_KEY, PREROLL_SUBTITLE, SUBSCRIPTION_ENABLED, } from '../constants';
import { appSessionDataProvider, localizationsProvider } from '../providers';
import { api, player } from '../singletons';
import { debug } from '../utils';
import { GooglePalSdk } from './GooglePalSdk';
import { NowPlayingPoller } from './NowPlayingPoller';
import { isValidMetadata, parseMetadata } from './utils';
export class StreamDataManager {
    #onMetadata;
    #nowPlayingPoller;
    #events;
    #streamData = null;
    #guideId;
    #isTopic = false;
    #listenId;
    #boostSessionId;
    #isBoostLoaded = false;
    #itemToken;
    #cachedNowPlayingNext;
    #nowPlaying = null;
    #instreamMetadata = null;
    #isInstreamMetadataValidated = false;
    #isOutstreamAdBreak = false;
    #isFetchingNewStreams = false;
    #googlePalSdk;
    constructor(options) {
        const { onMetadata } = options;
        this.#onMetadata = onMetadata;
        this.#nowPlayingPoller = new NowPlayingPoller(api.instance);
        this.#googlePalSdk = new GooglePalSdk();
        this.#events = {
            playing: this.#onPlaying,
            ended: this.#onStopped,
            stopped: this.#onStopped,
            pause: this.#onPause,
            adstart: this.#onAdStart,
            error: this.#onError,
            metadata: this.#onPlayerMetadata,
            waiting: this.#onWaiting,
            seeked: this.#onSeeked,
        };
        // Now Playing contains user-centric metadata such as favorites and ad eligibility flags, so we need to refresh
        // whenever auth tokens are updated (login/logout).
        api.on('authTokensUpdated', () => this.fetchNowPlayingOnce());
    }
    get #firstStream() {
        return this.#streamData?.streams?.[0];
    }
    get #isNowPlayingCurrent() {
        return this.#nowPlaying?.guideId === this.#guideId;
    }
    #registerEvents() {
        forEach(this.#events, (callback, event) => {
            player.off(event, callback);
            player.on(event, callback);
        });
    }
    #deregisterEvents() {
        forEach(this.#events, (callback, event) => {
            player.off(event, callback);
        });
    }
    #resendMetadataOnceOnPlaying = () => {
        // This is a catch for case when this hits for a boost transition.
        // When that happens we skip this update and reset the listener.
        if (player.isBoostTransitionStream) {
            player.off('playing', this.#resendMetadataOnceOnPlaying);
            player.once('playing', this.#resendMetadataOnceOnPlaying);
            return;
        }
        if (!this.#isInstreamMetadataValidated) {
            this.#sendMetadata();
        }
    };
    #getBaseMetadata(usePrimaryMetadata = false) {
        const primaryOrSecondary = usePrimaryMetadata ? 'primary' : 'secondary';
        // When fetching new streams, the current source in the player doesn't represent what's loading next, so we don't
        // want to send boost/intro metadata.
        if (!this.#isFetchingNewStreams && player.isBoostOrIntroStream) {
            const { title = '', subtitle = '', image = '', } = this.#nowPlaying?.boost?.[primaryOrSecondary] || {};
            return {
                ...this.#nowPlaying,
                title,
                subtitle,
                image: this.#nowPlaying?.primary?.image || '',
                boostImage: image,
                isBoostEnabled: api.getConfigSetting(BOOST_ENABLED, false),
                isBoostLoaded: this.#isBoostLoaded,
                isLive: true,
                canControlPlaybackRate: false,
            };
        }
        const { title = '', subtitle = '', image = '' } = this.#nowPlaying?.[primaryOrSecondary] || {};
        const isLive = (!this.#isTopic &&
            (!player.currentSource ||
                player.isIdle() ||
                player.isFailed() ||
                // Boost transitions are short audio clips between live stations, so it's a smoother UX to treat them as live
                player.isBoostTransitionStream ||
                player.isLive())) ??
            true; // Always fallback to true so that we're not rendering on-demand UI unless it's intentional
        return {
            ...this.#nowPlaying,
            title,
            subtitle,
            image,
            isLive,
            canControlPlaybackRate: this.#isTopic,
        };
    }
    #sendInstreamMetadata() {
        if (this.#instreamMetadata?.isAdBreak) {
            this.#onMetadata({
                ...this.#getBaseMetadata(),
                title: localizationsProvider.get(AD_BREAK_TITLE),
                subtitle: localizationsProvider.get(MIDROLL_SUBTITLE),
                image: this.#nowPlaying?.primary?.image || '',
                isAdBreak: false, // we only set isAdBreak to true for outstream ads, as it indicates a true break from content
            });
            return;
        }
        const { formattedTitle, image } = this.#instreamMetadata || {};
        const baseMetadata = this.#getBaseMetadata();
        const imageKey = player.isBoostStream ? 'boostImage' : 'image';
        this.#onMetadata({
            ...baseMetadata,
            title: formattedTitle || baseMetadata.title || '',
            [imageKey]: image || baseMetadata[imageKey] || '',
        });
    }
    #sendMetadata(usePrimaryMetadata = false, resendOnPlay = true) {
        if (!this.#nowPlaying) {
            return;
        }
        if (this.#isOutstreamAdBreak) {
            this.#onMetadata({
                ...this.#getBaseMetadata(),
                isLive: false,
                title: localizationsProvider.get(AD_BREAK_TITLE),
                subtitle: localizationsProvider.get(PREROLL_SUBTITLE),
                image: this.#nowPlaying?.primary?.image || '',
                canControlPlaybackRate: false,
                isAdBreak: true,
            });
            return;
        }
        if (usePrimaryMetadata) {
            // Due to the asynchronous nature of icy/id3 metadata, we want to emit just the primary now playing metadata initially
            this.#onMetadata(this.#getBaseMetadata(usePrimaryMetadata));
            if (resendOnPlay) {
                // Schedule the sending of the full metadata for when the player starts playing
                player.off('playing', this.#resendMetadataOnceOnPlaying);
                player.once('playing', this.#resendMetadataOnceOnPlaying);
            }
            return;
        }
        if (this.#instreamMetadata?.formattedTitle || this.#instreamMetadata?.isAdBreak) {
            this.#sendInstreamMetadata();
            return;
        }
        if (this.#nowPlaying) {
            this.#onMetadata(this.#getBaseMetadata());
        }
    }
    async #transformMetadata(metadata) {
        const { title, subtitle, image, isAdBreak, usePrimaryMetadata } = metadata;
        const transformedMetadata = {
            ...metadata,
            formattedTitle: title ? `${title} - ${subtitle}` : '',
        };
        // Don't fetch artwork if it's already present. in ad break, or primary metadata is being used
        if (image || isAdBreak || usePrimaryMetadata) {
            return transformedMetadata;
        }
        const imageUrl = await api.streams.getArtworkUrl(title, subtitle);
        if (!imageUrl) {
            return transformedMetadata;
        }
        const metadataWithImage = { ...transformedMetadata, image: imageUrl };
        debug('Transformed metadata', metadataWithImage);
        return metadataWithImage;
    }
    #onPlayerMetadata = async (metadata) => {
        if (!(metadata?.icy || metadata?.id3)) {
            return;
        }
        // ID3 metadata is not gated in Web Media Player, as it's a native feature in hls.js, so we'll guard against metadata
        // processing here for streams that aren't granted explicit stream metadata permissions from the Tune API.
        if (!player.currentSource?.useStreamMetadata) {
            return;
        }
        const processStartMs = Date.now();
        const currentListenId = this.#listenId;
        const currentBoostSessionId = this.#boostSessionId;
        // Guarantee a fallback empty metadata override to prevent NP metadata from being sent during async artwork fetch
        this.#instreamMetadata = this.#instreamMetadata || {};
        const parsedMetadata = parseMetadata(metadata, this.#isInstreamMetadataValidated);
        const isValid = isValidMetadata(parsedMetadata, this.#instreamMetadata);
        // Explicit check for `parsedMetadata` helps TypeScript inference below
        if (!(parsedMetadata && isValid)) {
            return;
        }
        // If Now Playing is not current, the Now Playing callback will handle stopping the poller when it receives new data
        if (this.#isNowPlayingCurrent && this.#nowPlayingPoller.isRunning) {
            this.stopNowPlayingPoller();
        }
        this.#isInstreamMetadataValidated = true;
        this.#instreamMetadata = await this.#transformMetadata(parsedMetadata);
        const { delayMs, startMs } = this.#instreamMetadata;
        // Player state can change during artwork fetch. Verify that the active session hasn't changed before continuing.
        if (!(player.isLoading() || player.isLoaded() || player.isPlaying()) ||
            currentListenId !== this.#listenId ||
            currentBoostSessionId !== this.#boostSessionId) {
            return;
        }
        // Delay sending metadata, as defined by the associated metadata parser.
        // See InstreamMetadata type for more info, as well as the metadata parsers in utils/metadata.
        if (delayMs || startMs) {
            const offsetMs = Date.now() - processStartMs;
            const currentTimeMs = player.getCurrentTime() * 1000;
            const derivedDelayMs = delayMs || startMs - currentTimeMs;
            const effectiveDelayMs = derivedDelayMs - offsetMs;
            if (effectiveDelayMs > 0) {
                await wait(effectiveDelayMs);
            }
        }
        // Player state can change during delay. Verify active session hasn't changed before continuing.
        if (!(player.isLoading() || player.isLoaded() || player.isPlaying()) ||
            currentListenId !== this.#listenId ||
            currentBoostSessionId !== this.#boostSessionId) {
            return;
        }
        if (this.#instreamMetadata?.usePrimaryMetadata) {
            this.#sendMetadata(true, false);
        }
        else {
            this.#sendMetadata();
        }
    };
    #onNowPlayingMetadata = (metadata, sendPrimaryMetadata = false) => {
        if (!metadata) {
            return;
        }
        // Instream metadata supersedes Now Playing metadata, so we don't need to continue polling Now Playing
        if (player.isFailed() || this.#instreamMetadata) {
            this.stopNowPlayingPoller();
        }
        this.#nowPlaying = metadata;
        if (this.#firstStream?.isLiveSeekStream) {
            this.#nowPlaying.isLiveSeek = true;
        }
        if (this.#cachedNowPlayingNext) {
            this.#nowPlaying.next = this.#cachedNowPlayingNext;
        }
        else if (this.#firstStream?.nextGuideId) {
            this.#nowPlaying.next = {
                guideId: this.#firstStream?.nextGuideId,
            };
        }
        else if (metadata.boost) {
            this.#nowPlaying.next = player.isBoostOrIntroStream
                ? metadata.primary
                : metadata.boost.primary;
        }
        else if (!metadata.boost) {
            this.#isBoostLoaded = false;
        }
        this.#nowPlaying.isBoostEnabled = Boolean(this.#guideId &&
            metadata.boost &&
            api.getConfigSetting(BOOST_ENABLED) &&
            !this.#nowPlaying.isLiveSeek);
        this.#nowPlaying.isBoostLoaded = this.#isBoostLoaded;
        this.#sendMetadata(sendPrimaryMetadata);
    };
    #onPlaying = () => {
        if (!(this.#nowPlayingPoller.isRunning || this.#instreamMetadata) &&
            !player.isBoostTransitionStream) {
            this.#startNowPlayingPoller();
        }
    };
    #onWaiting = () => {
        if (this.#isNowPlayingCurrent && this.#nowPlayingPoller.isRunning) {
            this.stopNowPlayingPoller();
        }
    };
    #onPause = () => {
        if (this.#nowPlayingPoller.isRunning) {
            this.stopNowPlayingPoller();
        }
    };
    #onStopped = () => {
        if (!player.isFailed()) {
            // We want to allow at least 1 NP fetch to complete for failed states. The NP listener will handle stopping the poller.
            this.stopNowPlayingPoller();
        }
        this.#resetInstreamMetadata();
        this.#sendMetadata(true, false);
    };
    #onAdStart = () => {
        this.#onStopped();
    };
    #onError = () => {
        if (player.isFailed()) {
            this.#onStopped();
        }
    };
    #onSeeked = () => {
        if (this.#nowPlaying?.isLiveSeek) {
            this.#sendMetadata();
        }
    };
    #resetInstreamMetadata() {
        this.#isInstreamMetadataValidated = false;
        this.#instreamMetadata = null;
    }
    #startNowPlayingPoller = (initialDelay) => {
        this.#nowPlayingPoller.start({
            guideId: this.#guideId,
            itemToken: this.#itemToken,
            onMetadata: this.#onNowPlayingMetadata,
            initialDelay,
        });
    };
    #maybeApplySubscriptionMetadata() {
        const isSubscriptionEnabled = api.getConfigSetting(SUBSCRIPTION_ENABLED);
        const isQrCodeEnabled = api.getConfigSetting(PLAYER_SUBSCRIPTION_QR_CODE_ENABLED);
        const imageUrl = api.getConfigSetting(PLAYER_SUBSCRIPTION_QR_CODE_IMAGE_URL);
        const labelKey = api.getConfigSetting(PLAYER_SUBSCRIPTION_QR_CODE_LABEL_KEY);
        if (isSubscriptionEnabled && isQrCodeEnabled && imageUrl && labelKey) {
            this.#onMetadata({
                ...this.#nowPlaying,
                title: localizationsProvider.get(labelKey, 'SCAN TO UPGRADE'),
                image: imageUrl,
            });
            return;
        }
        this.#sendMetadata(true);
    }
    get streamData() {
        return this.#streamData;
    }
    stopNowPlayingPoller() {
        this.#nowPlayingPoller.stop();
    }
    /**
     * When `options` is defined, immediately send primary metadata (for an optimistic UI update),
     * and then schedule another call to sendBoostMetadata without `options` to send the secondary
     * metadata for either the boost transition stream or the boost/directory stream.
     */
    sendBoostMetadata(options) {
        if (!this.#nowPlaying) {
            return;
        }
        const { isBoostOrIntroStream, isBoostTransition } = options;
        this.#resetInstreamMetadata();
        this.stopNowPlayingPoller();
        this.#boostSessionId = uuidv4();
        // Cache for later use by NP listener
        this.#isBoostLoaded = isBoostOrIntroStream;
        // Update current NP
        this.#nowPlaying.isBoostLoaded = this.#isBoostLoaded;
        const baseMetadata = isBoostOrIntroStream ? this.#nowPlaying?.boost : this.#nowPlaying;
        const { title: baseTitle = '', subtitle = '' } = baseMetadata?.primary || {};
        const transitionTitle = localizationsProvider.get(isBoostOrIntroStream ? BOOST_INTRO_TITLE : BOOST_OUTRO_TITLE);
        this.#onMetadata({
            ...this.#nowPlaying,
            title: isBoostTransition ? transitionTitle : baseTitle,
            subtitle,
            image: this.#nowPlaying?.primary?.image,
            isBoostEnabled: true,
            isLive: true,
        });
    }
    setOutstreamAdBreak(isOutstreamAdBreak) {
        this.#isOutstreamAdBreak = isOutstreamAdBreak;
        if (this.#isOutstreamAdBreak) {
            this.stopNowPlayingPoller();
            this.#sendMetadata();
        }
        else {
            this.#sendMetadata(true);
        }
    }
    async fetchStreams(options) {
        const { guideItem, loadOptions, listenId, isReplayingBoostStream } = options;
        const { guideId, itemToken, next } = guideItem;
        this.#isFetchingNewStreams = true;
        this.#isBoostLoaded = isReplayingBoostStream;
        this.#guideId = guideId;
        this.#isTopic = isTopic(guideId);
        this.#listenId = listenId;
        this.#itemToken = itemToken;
        this.#cachedNowPlayingNext = next;
        this.stopNowPlayingPoller();
        this.#resetInstreamMetadata();
        this.#registerEvents();
        const isPremium = api.isPremium;
        const streamsOptions = {
            // Delegating Now Playing fetching to the poller for Premium users as a minor network latency optimization.
            // Free users require Now Playing data upfront to determine preroll eligibility before loading the stream.
            requestNowPlaying: !isPremium,
            params: {
                listenId,
                itemToken,
                id: guideId,
                ...(!isPremium && { paln: await this.#googlePalSdk.getNonce() }),
                ...appSessionDataProvider.streamsConsentParams,
            },
            playbackPositionOverride: loadOptions?.playbackPositionOverride,
        };
        try {
            this.#streamData = await api.streams.get(streamsOptions);
        }
        catch (error) {
            if (error instanceof Error) {
                error.message = `Stream data fetch error: ${error.message}`;
            }
            throw error;
        }
        if (this.#streamData.subscriptionRequiredStream) {
            this.#onNowPlayingMetadata(this.#streamData.nowPlaying);
            this.#deregisterEvents();
            this.#maybeApplySubscriptionMetadata();
            return;
        }
        if (this.#streamData.nowPlaying) {
            this.#onNowPlayingMetadata(this.#streamData.nowPlaying, true);
        }
        // Begins poller immediately if Now Playing wasn't requested synchronously above, delayed by ttl otherwise
        this.#startNowPlayingPoller(this.#streamData.nowPlaying?.ttl);
        this.#isFetchingNewStreams = false;
    }
    async fetchAutoplayGuideItem() {
        try {
            const guideItem = await api.ui.get({ guideId: 'autoplay' });
            // Get the first playable ChildGuideItem in the Autoplay container
            const autoplayGuideItem = guideItem?.containers
                ?.find((container) => container.containerType === 'Autoplay')
                ?.children.slice(0, 3)
                .find((childGuideItem) => childGuideItem.playableGuideId) || null;
            const { playableGuideId, itemToken } = autoplayGuideItem || {};
            if (!playableGuideId) {
                return;
            }
            this.#guideId = playableGuideId;
            const nowPlaying = await this.#nowPlayingPoller.fetchOnce({
                guideId: playableGuideId,
                itemToken,
            });
            if (nowPlaying) {
                this.#onNowPlayingMetadata(nowPlaying, true);
            }
            return autoplayGuideItem;
        }
        catch {
            // do nothing
        }
    }
    fetchNowPlayingOnce = async (guideItem) => {
        if (!(this.#guideId || guideItem)) {
            return;
        }
        this.#guideId = guideItem?.playableGuideId || this.#guideId;
        this.#itemToken = guideItem?.itemToken || this.#itemToken;
        const nowPlaying = await this.#nowPlayingPoller.fetchOnce({
            guideId: this.#guideId,
            itemToken: this.#itemToken,
        });
        if (nowPlaying) {
            this.#onNowPlayingMetadata(nowPlaying);
        }
    };
}
